import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useGraphEditorStore } from '@/graph-store';
import {
  computeDeletePlan,
  computeBulkMaterialDeletePlan,
  computeMaterialDeletePlan,
} from '@/graph-store/actions/deleteActions';
import type {
  BulkMaterialDeletePlan,
  DeletePlan,
  MaterialDeletePlan,
} from '@/graph-store/actions/deleteActions';

export interface DeleteTarget {
  kind: 'operation' | 'material';
  operationId?: string;
  materialId?: string;
  materialIds?: string[];
}

export interface DeleteConfirmationDialogProps {
  target: DeleteTarget;
  onConfirm: () => void;
  onCancel: () => void;
}

export function DeleteConfirmationDialog({
  target,
  onConfirm,
  onCancel,
}: DeleteConfirmationDialogProps) {
  const graphData = useGraphEditorStore((s) => s.graphData);

  const impact = useMemo(() => {
    if (target.kind === 'operation' && target.operationId) {
      return computeDeletePlan(target.operationId, graphData);
    }
    return null;
  }, [target, graphData]);

  const materialImpact = useMemo(() => {
    if (target.kind !== 'material' || !target.materialId || (target.materialIds?.length ?? 0) > 1) return null;
    try {
      return computeMaterialDeletePlan(target.materialId, graphData);
    } catch {
      return null;
    }
  }, [target, graphData]);

  const bulkMaterialImpact = useMemo(() => {
    if (target.kind !== 'material' || (target.materialIds?.length ?? 0) <= 1) return null;
    try {
      return computeBulkMaterialDeletePlan(target.materialIds!, graphData);
    } catch {
      return null;
    }
  }, [target, graphData]);

  const hasMaterialCascade =
    target.kind === 'material' &&
    !!materialImpact &&
    (materialImpact.operationsToRemove.length > 0 ||
      materialImpact.materialsToRemove.length > 1);

  const entityName =
    target.kind === 'operation'
      ? graphData.operationNodes[target.operationId!]?.displayName ?? 'this step'
      : graphData.materialNodes[target.materialId!]?.name ?? 'this material';

  const selectedMaterialNames =
    target.kind === 'material' && target.materialIds?.length
      ? target.materialIds.map((id) => graphData.materialNodes[id]?.name ?? id)
      : [];

  const title =
    target.kind === 'operation'
      ? 'Delete this step?'
      : target.materialIds && target.materialIds.length > 1
        ? 'Delete these materials?'
        : 'Delete this material?';

  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" gutterBottom>
          {target.kind === 'operation'
            ? `Step: ${entityName}`
            : target.materialIds && target.materialIds.length > 1
              ? `Materials: ${selectedMaterialNames.join(', ')}`
              : `Material: ${entityName}`}
        </Typography>

        {impact && <DeleteImpactPreview plan={impact} graphData={graphData} />}

        {target.kind === 'material' &&
          materialImpact &&
          hasMaterialCascade &&
          target.materialId && (
            <MaterialDeleteImpactPreview
              materialId={target.materialId}
              plan={materialImpact}
              graphData={graphData}
            />
          )}

        {target.kind === 'material' &&
          bulkMaterialImpact &&
          target.materialIds &&
          target.materialIds.length > 1 && (
            <BulkMaterialDeleteImpactPreview
              selectedMaterialIds={target.materialIds}
              plan={bulkMaterialImpact}
              graphData={graphData}
            />
          )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" color="error" onClick={onConfirm}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BulkMaterialDeleteImpactPreview({
  selectedMaterialIds,
  plan,
  graphData,
}: {
  selectedMaterialIds: string[];
  plan: BulkMaterialDeletePlan;
  graphData: ReturnType<typeof useGraphEditorStore.getState>['graphData'];
}) {
  const selectedSet = new Set(selectedMaterialIds);
  const additionalMaterialIds = plan.materialsToRemove.filter((id) => !selectedSet.has(id));
  const additionalMaterialNames = additionalMaterialIds.map(
    (id) => graphData.materialNodes[id]?.name ?? id,
  );
  const stepNames = plan.operationsToRemove.map(
    (id) => graphData.operationNodes[id]?.displayName ?? id,
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>
        Deleting {selectedMaterialIds.length} materials will also delete {plan.operationsToRemove.length} step(s) and{' '}
        {additionalMaterialIds.length} additional material(s):
      </Typography>

      {stepNames.length > 0 && (
        <Typography
          variant="body2"
          component="ul"
          sx={{ pl: 2, mt: 1, mb: 0 }}
        >
          {stepNames.map((name, index) => (
            <li key={`bulk-step-${index}-${name}`}>Step: {name}</li>
          ))}
        </Typography>
      )}

      {additionalMaterialNames.length > 0 && (
        <Typography
          variant="body2"
          component="ul"
          sx={{ pl: 2, mt: 1, mb: 0 }}
        >
          {additionalMaterialNames.map((name, index) => (
            <li key={`bulk-material-${index}-${name}`}>Material: {name}</li>
          ))}
        </Typography>
      )}
    </Box>
  );
}

function MaterialDeleteImpactPreview({
  materialId,
  plan,
  graphData,
}: {
  materialId: string;
  plan: MaterialDeletePlan;
  graphData: ReturnType<typeof useGraphEditorStore.getState>['graphData'];
}) {
  const additionalMaterialIds = plan.materialsToRemove.filter((id) => id !== materialId);
  const additionalMaterialNames = additionalMaterialIds.map(
    (id) => graphData.materialNodes[id]?.name ?? id,
  );
  const stepNames = plan.operationsToRemove.map(
    (id) => graphData.operationNodes[id]?.displayName ?? id,
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>
        Deleting this material will also delete {plan.operationsToRemove.length} step(s) and{' '}
        {additionalMaterialIds.length} material(s):
      </Typography>

      {stepNames.length > 0 && (
        <Typography
          variant="body2"
          component="ul"
          sx={{ pl: 2, mt: 1, mb: 0 }}
        >
          {stepNames.map((name, index) => (
            <li key={`step-${index}-${name}`}>Step: {name}</li>
          ))}
        </Typography>
      )}

      {additionalMaterialNames.length > 0 && (
        <Typography
          variant="body2"
          component="ul"
          sx={{ pl: 2, mt: 1, mb: 0 }}
        >
          {additionalMaterialNames.map((name, index) => (
            <li key={`material-${index}-${name}`}>Material: {name}</li>
          ))}
        </Typography>
      )}
    </Box>
  );
}

function DeleteImpactPreview({
  plan,
  graphData,
}: {
  plan: DeletePlan;
  graphData: ReturnType<typeof useGraphEditorStore.getState>['graphData'];
}) {
  const materialNames = plan.materialsToRemove.map(
    (id) => graphData.materialNodes[id]?.name ?? id,
  );
  const stepNames = plan.operationsToRemove.map(
    (id) => graphData.operationNodes[id]?.displayName ?? id,
  );

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>
        Deleting this step will also delete {plan.operationsToRemove.length} step(s) and{' '}
        {plan.materialsToRemove.length} material(s):
      </Typography>

      {stepNames.length > 0 && (
        <Typography variant="body2" component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
          {stepNames.map((name, index) => (
            <li key={`step-${index}-${name}`}>Step: {name}</li>
          ))}
        </Typography>
      )}

      {materialNames.length > 0 && (
        <Typography variant="body2" component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
          {materialNames.map((name, index) => (
            <li key={`material-${index}-${name}`}>Material: {name}</li>
          ))}
        </Typography>
      )}
    </Box>
  );
}

export default DeleteConfirmationDialog;
